/**
 * A conservative "did you mean …?" suggestion for a mistyped email domain.
 * Curated typo → fix map (plus a generic `.com` misspelling rule), deliberately
 * NOT fuzzy edit-distance — matching by distance wrongly "corrects" real domains
 * (email.com → gmail.com). Returns null when there's nothing safe to suggest.
 * This is only a hint; the server's domain check + bounce handling are the real
 * guardrails.
 */
const DOMAIN_TYPOS: Record<string, string> = {
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gmil.com": "gmail.com",
  "gnail.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gmailc.om": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.cm": "gmail.com",
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yahoo.co": "yahoo.com",
  "hotmial.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "hotnail.com": "hotmail.com",
  "hotmail.co": "hotmail.com",
  "outlok.com": "outlook.com",
  "outook.com": "outlook.com",
  "outlook.co": "outlook.com",
  "iclould.com": "icloud.com",
  "iclod.com": "icloud.com",
  "icloud.co": "icloud.com",
  "live.co": "live.com",
};

// Clear TLD misspellings of ".com". ".co" is a real TLD, so it's excluded here.
const COM_TYPO = /\.(con|cmo|ocm|comm|vom|xom|cin|cim)$/;

export function suggestEmail(raw: string): string | null {
  const email = raw.trim().toLowerCase();
  const at = email.lastIndexOf("@");
  if (at < 1 || at === email.length - 1) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (!domain.includes(".")) return null;

  if (DOMAIN_TYPOS[domain]) return `${local}@${DOMAIN_TYPOS[domain]}`;

  const comFixed = domain.replace(COM_TYPO, ".com");
  if (comFixed !== domain) return `${local}@${comFixed}`;

  return null;
}
